/**
 * OccupationalRisk Service
 * API integration for occupational risk CRUD operations
 *
 * @version 2025.1.0
 * @description Serviço de integração com API de riscos ocupacionais (NR-09, NR-15, NR-16)
 */
import apiClient from './apiClient';
import {
  OccupationalRisk,
  OccupationalRiskApiResponse,
  OccupationalRiskFilters,
  OccupationalRiskFormData,
  OccupationalRiskApiPayload,
  OccupationalRiskListResponse,
  OccupationalRiskDependencies,
  OccupationalRiskClassification,
  EsocialAgentCodeBulkEntry,
  RiskType,
} from '../types/occupationalRisk';

// Re-export types for convenience
export * from '../types/occupationalRisk';

export type {
  UnresolvedTerm as OccupationalRiskImportUnresolvedTerm,
  AvailableOption as OccupationalRiskImportAvailableOption,
  ImportPreviewResult as OccupationalRiskImportPreviewResult,
} from '../types/import';
import type { ImportPreviewResult as OccupationalRiskImportPreviewResult } from '../types/import';

export interface OccupationalRiskImportMappings {
  tipo_de_risco?: Record<string, string>;
}

export interface OccupationalRiskListParams extends OccupationalRiskFilters {
  page?: number;
  limit?: number;
}

// Marcação sim/não: `null` desmarca; `undefined` (campo fora do envio) não toca na coluna, o que
// protege a atualização parcial.
function rotuloDaMarcacao(marcada: boolean | undefined, rotulo: string): string | null | undefined {
  if (marcada === undefined) return undefined;
  return marcada ? rotulo : null;
}

class OccupationalRiskService {
  private baseUrl = '/occupational-risks';

  /**
   * Convert form data (with booleans) to API payload (with string codes)
   */
  private formDataToApiPayload(formData: OccupationalRiskFormData): OccupationalRiskApiPayload {
    return {
      risk: formData.risk,
      riskFactorName: formData.riskFactorName,
      hazard: formData.hazard,
      riskType: formData.riskType,
      esocialAgentCode: formData.esocialAgentCode ?? undefined,
      esocialAgentCodeNotApplicable: formData.esocialAgentCodeNotApplicable ?? undefined,
      actionLevel: formData.actionLevel ?? undefined,
      toleranceLimit: formData.toleranceLimit ?? undefined,
      measurementUnit: formData.measurementUnit ?? undefined,
      evaluationMethod: formData.evaluationMethod ?? undefined,
      defaultPeriodicity: formData.defaultPeriodicity ?? undefined,
      penetrationRoute: formData.penetrationRoute ?? undefined,
      propagationMedium: formData.propagationMedium ?? undefined,
      unhealthyAgent: rotuloDaMarcacao(formData.unhealthyAgent, 'NR-15'),
      hazardousAgent: rotuloDaMarcacao(formData.hazardousAgent, 'NR-16'),
      socialSecurityAgent: rotuloDaMarcacao(formData.socialSecurityAgent, 'NR-09'),
      evaluationMethodology: formData.evaluationMethodology ?? undefined,
      legalFoundation: formData.legalFoundation ?? undefined,
      healthDamages: formData.healthDamages ?? undefined,
      determiningFactors: formData.determiningFactors ?? undefined,
      emergencyPreparation: formData.emergencyPreparation ?? undefined,
      exposureForm: formData.exposureForm ?? undefined,
      exposureType: formData.exposureType ?? undefined,
      riskMatrixType: formData.riskMatrixType ?? undefined,
      riskProbability: formData.riskProbability ?? undefined,
      riskSeverity: formData.riskSeverity ?? undefined,
      unhealthyDegree: formData.unhealthyDegree ?? undefined,
      assessmentType: formData.assessmentType ?? undefined,
      hazardousAgentId: formData.hazardousAgentId ?? null,
      inssAgentCode: formData.inssAgentCode ?? undefined,
      nr15Annex: formData.nr15Annex ?? null,
      nr16Annex: formData.nr16Annex ?? undefined,
      specialRetirementYears: formData.specialRetirementYears ?? null,
      ceilingValue: formData.ceilingValue,
      skinAbsorption: formData.skinAbsorption,
      status: formData.status,
    };
  }

  /**
   * Convert API response to form data (convert strings to booleans for checkboxes)
   */
  apiResponseToFormData(apiData: OccupationalRiskApiResponse): Partial<OccupationalRiskFormData> {
    return {
      risk: apiData.risk,
      riskFactorName: apiData.riskFactorName,
      hazard: apiData.hazard,
      riskType: apiData.riskType,
      esocialAgentCode: apiData.esocialAgentCode ?? undefined,
      esocialAgentCodeNotApplicable: apiData.esocialAgentCodeNotApplicable ?? false,
      actionLevel: apiData.actionLevel ?? undefined,
      toleranceLimit: apiData.toleranceLimit ?? undefined,
      measurementUnit: apiData.measurementUnit ?? undefined,
      evaluationMethod: apiData.evaluationMethod ?? undefined,
      defaultPeriodicity: apiData.defaultPeriodicity ?? undefined,
      penetrationRoute: apiData.penetrationRoute ?? undefined,
      propagationMedium: apiData.propagationMedium ?? undefined,
      // Convert string codes to boolean for checkboxes
      unhealthyAgent: Boolean(apiData.unhealthyAgent),
      hazardousAgent: Boolean(apiData.hazardousAgent),
      socialSecurityAgent: Boolean(apiData.socialSecurityAgent),
      evaluationMethodology: apiData.evaluationMethodology ?? undefined,
      legalFoundation: apiData.legalFoundation ?? undefined,
      healthDamages: apiData.healthDamages ?? undefined,
      determiningFactors: apiData.determiningFactors ?? undefined,
      emergencyPreparation: apiData.emergencyPreparation ?? undefined,
      exposureForm: apiData.exposureForm ?? undefined,
      exposureType: apiData.exposureType ?? undefined,
      riskMatrixType: apiData.riskMatrixType ?? null,
      riskProbability: apiData.riskProbability ?? null,
      riskSeverity: apiData.riskSeverity ?? null,
      unhealthyDegree: apiData.unhealthyDegree ?? undefined,
      assessmentType: apiData.assessmentType ?? undefined,
      hazardousAgentId: apiData.hazardousAgentId ?? null,
      inssAgentCode: apiData.inssAgentCode ?? undefined,
      nr15Annex: apiData.nr15Annex ?? null,
      nr16Annex: apiData.nr16Annex ?? undefined,
      specialRetirementYears: apiData.specialRetirementYears ?? null,
      ceilingValue: apiData.ceilingValue ?? false,
      skinAbsorption: apiData.skinAbsorption ?? false,
      status: apiData.status,
    };
  }

  /**
   * Get paginated list of occupational risks
   */
  async getOccupationalRisks(params: OccupationalRiskListParams = {}): Promise<OccupationalRiskListResponse> {
    const { page = 1, limit = 20, ...filters } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.search) queryParams.append('search', filters.search);
    if (filters.riskType) queryParams.append('riskType', filters.riskType);
    if (filters.unhealthyAgent !== undefined) queryParams.append('unhealthyAgent', String(filters.unhealthyAgent));
    if (filters.hazardousAgent !== undefined) queryParams.append('hazardousAgent', String(filters.hazardousAgent));
    if (filters.socialSecurityAgent !== undefined) queryParams.append('socialSecurityAgent', String(filters.socialSecurityAgent));
    if (filters.esocialAgentCode !== undefined) queryParams.append('esocialAgentCode', String(filters.esocialAgentCode));
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
    if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`${this.baseUrl}?${queryParams.toString()}`);

    // Backend returns: { data: { risks, total, page, limit, totalPages } }
    const backendData = response.data.data || response.data;

    return {
      risks: backendData.risks ?? [],
      total: backendData.total ?? 0,
      page: backendData.page || page,
      limit: backendData.limit || limit,
      totalPages: backendData.totalPages || Math.ceil((backendData.total ?? 0) / limit),
    };
  }

  /**
   * Get occupational risk by ID
   */
  async getOccupationalRiskById(id: string | number): Promise<OccupationalRisk> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data.data ?? response.data;
  }

  /**
   * Create new occupational risk
   */
  async createOccupationalRisk(data: OccupationalRiskFormData): Promise<OccupationalRisk> {
    const payload = this.formDataToApiPayload(data);
    const response = await apiClient.post(this.baseUrl, payload);
    return response.data.data || response.data;
  }

  /**
   * Update occupational risk by ID
   */
  async updateOccupationalRisk(id: string | number, data: Partial<OccupationalRiskFormData>): Promise<OccupationalRisk> {
    const payload = this.formDataToApiPayload(data as OccupationalRiskFormData);
    const response = await apiClient.put(`${this.baseUrl}/${id}`, payload);
    return response.data.data || response.data;
  }

  /**
   * #473 — conclui o código da Tabela 24 de vários riscos de uma vez.
   *
   * Uma chamada para o painel inteiro, nunca uma por linha: o backend reprova o lote
   * completo quando uma entrada é inválida, e é isso que impede o mutirão de terminar com
   * metade dos riscos gravados. O erro sobe como veio, com o risco e o motivo.
   */
  async updateEsocialAgentCodeBulk(itens: EsocialAgentCodeBulkEntry[]): Promise<void> {
    await apiClient.patch(`${this.baseUrl}/esocial-agent-code/bulk`, { itens });
  }

  /**
   * Delete occupational risk by ID (soft delete)
   */
  async deleteOccupationalRisk(id: string | number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Activate occupational risk
   */
  async activateOccupationalRisk(id: string | number): Promise<OccupationalRisk> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/activate`);
    return response.data.data || response.data;
  }

  /**
   * Deactivate occupational risk
   */
  async deactivateOccupationalRisk(id: string | number): Promise<OccupationalRisk> {
    const response = await apiClient.patch(`${this.baseUrl}/${id}/deactivate`);
    return response.data.data || response.data;
  }

  /**
   * Get risks by type
   */
  async getByType(type: RiskType): Promise<OccupationalRisk[]> {
    const response = await apiClient.get(`${this.baseUrl}/type/${type}`);
    return response.data.data || response.data;
  }

  /**
   * Get hazardous risks (NR-16)
   */
  async getHazardousRisks(): Promise<OccupationalRisk[]> {
    const response = await apiClient.get(`${this.baseUrl}/hazardous`);
    return response.data.data || response.data;
  }

  /**
   * Get unhealthy risks (NR-15)
   */
  async getUnhealthyRisks(): Promise<OccupationalRisk[]> {
    const response = await apiClient.get(`${this.baseUrl}/unhealthy`);
    return response.data.data || response.data;
  }

  /**
   * Check dependencies before deletion
   */
  async checkDependencies(id: string | number): Promise<OccupationalRiskDependencies> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dependencies`);
    return response.data.data || response.data;
  }

  /**
   * Convert string risk type to RiskType enum
   */
  private stringToRiskType(typeString: string): RiskType {
    const typeMap: Record<string, RiskType> = {
      'physical': RiskType.PHYSICAL,
      'chemical': RiskType.CHEMICAL,
      'biological': RiskType.BIOLOGICAL,
      'ergonomic': RiskType.ERGONOMIC,
      'accident': RiskType.ACCIDENT,
      'other': RiskType.OTHER,
    };

    return typeMap[typeString.toLowerCase()] || RiskType.OTHER;
  }

  /**
   * Get risk classification (calculated risk type and regulatory info)
   * This is a helper method that processes the risk data client-side
   * Note: Backend already returns this in the classification field
   */
  getRiskClassification(risk: OccupationalRisk): OccupationalRiskClassification {
    // If backend already provided classification, use it
    if (risk.classification) {
      const regulatoryFramework: string[] = [];
      if (risk.hazardousAgent) regulatoryFramework.push('NR-16');
      if (risk.unhealthyAgent) regulatoryFramework.push('NR-15');
      if (risk.socialSecurityAgent) regulatoryFramework.push('NR-09');
      if (risk.legalFoundation) {
        const frameworks = risk.legalFoundation.match(/NR-\d+|Lei\s+\d+|Decreto\s+\d+|Portaria\s+\d+/gi);
        if (frameworks) {
          frameworks.forEach(f => {
            if (!regulatoryFramework.includes(f.toUpperCase())) {
              regulatoryFramework.push(f.toUpperCase());
            }
          });
        }
      }

      return {
        type: this.stringToRiskType(risk.classification.type),
        isHazardous: risk.classification.isHazardous,
        isUnhealthy: risk.classification.isUnhealthy,
        requiresSocialSecurity: risk.classification.hasSocialSecurityAgent,
        regulatoryFramework,
      };
    }

    // Fallback: calculate client-side
    const type = this.calculateRiskType(risk);
    const regulatoryFramework: string[] = [];

    if (risk.hazardousAgent) regulatoryFramework.push('NR-16');
    if (risk.unhealthyAgent) regulatoryFramework.push('NR-15');
    if (risk.socialSecurityAgent) regulatoryFramework.push('NR-09');
    if (risk.legalFoundation) {
      const frameworks = risk.legalFoundation.match(/NR-\d+|Lei\s+\d+|Decreto\s+\d+|Portaria\s+\d+/gi);
      if (frameworks) {
        frameworks.forEach(f => {
          if (!regulatoryFramework.includes(f.toUpperCase())) {
            regulatoryFramework.push(f.toUpperCase());
          }
        });
      }
    }

    return {
      type,
      isHazardous: Boolean(risk.hazardousAgent),
      isUnhealthy: Boolean(risk.unhealthyAgent),
      requiresSocialSecurity: Boolean(risk.socialSecurityAgent),
      regulatoryFramework,
    };
  }

  /**
   * Calculate risk type based on agent and description
   * This mimics the backend Model method formatRiskType()
   */
  private calculateRiskType(risk: OccupationalRisk): RiskType {
    const agent = risk.hazard?.toLowerCase() || '';
    const riskDesc = risk.risk?.toLowerCase() || '';
    const combined = `${agent} ${riskDesc}`;

    // Physical risks
    if (
      /ruído|vibração|radiação|temperatura|pressão|umidade|calor|frio/i.test(combined)
    ) {
      return RiskType.PHYSICAL;
    }

    // Chemical risks
    if (
      /químico|poeira|fumo|névoa|neblina|gás|vapor|produto químico|substância/i.test(combined)
    ) {
      return RiskType.CHEMICAL;
    }

    // Biological risks
    if (
      /biológico|vírus|bactéria|fungo|parasita|microorganismo/i.test(combined)
    ) {
      return RiskType.BIOLOGICAL;
    }

    // Ergonomic risks
    if (
      /ergonômico|postura|esforço|repetitivo|levantamento|movimentação|monotonia|ritmo/i.test(combined)
    ) {
      return RiskType.ERGONOMIC;
    }

    // Accident risks
    if (
      /acidente|mecânico|queda|corte|perfuração|choque|incêndio|explosão|máquina|equipamento/i.test(combined)
    ) {
      return RiskType.ACCIDENT;
    }

    return RiskType.OTHER;
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await apiClient.get(`${this.baseUrl}/health`);
    return response.data.data || response.data;
  }

  /**
   * Download import template
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/template`, { responseType: 'blob' });
    return response as unknown as Blob;
  }

  /**
   * Preview import from Excel
   */
  async previewImport(file: File): Promise<OccupationalRiskImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`${this.baseUrl}/import/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Import occupational risks from Excel
   */
  async importRisks(file: File, options?: { mappings?: OccupationalRiskImportMappings }): Promise<{ imported: number; skipped?: number; errors: Array<{ row: number; error: string }> }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.mappings) formData.append('mappings', JSON.stringify(options.mappings));
    const response = await apiClient.post(`${this.baseUrl}/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data ?? response.data;
  }

  /**
   * Export occupational risks to Excel
   */
  async exportRisks(): Promise<Blob> {
    const response = await apiClient.get(`${this.baseUrl}/export`, { responseType: 'blob' });
    return response as unknown as Blob;
  }
}

// Export singleton instance
export const occupationalRiskService = new OccupationalRiskService();
export default occupationalRiskService;
